// QueryContext.jsx — VERSIÓN WARP ⚡ (Star Trek Edition)
import React, {
  createContext, useContext, useState, useCallback,
  useMemo, useRef, useEffect,
} from 'react';
import apiClient from './api/client';
import { loadParquetZip, loadParquetDirect } from './utils/parquetLoader';
import { useAuth } from './AuthContext';

const QueryContext = createContext();
export const useQuery = () => useContext(QueryContext);

// ─── Cache global de configs (compartido entre renders) ─────────
const CONFIG_CACHE = new Map();
const CONFIG_TTL = 5 * 60 * 1000;

// ─── Cache ligero para autocomplete (30s) ───────────────────────
const AUTOCOMPLETE_CACHE = new Map();
const AUTOCOMPLETE_TTL = 30 * 1000;

// ─── Rutas que devuelven parquet "directo" ──────────────────────
const PARQUET_DIRECT_PREFIXES = ['/inventario/', '/inventario-farmatodo/'];
const isDirectParquetPath = (path) =>
  PARQUET_DIRECT_PREFIXES.some((p) => path.includes(p));

// ═══════════════════════════════════════════════════════════════
// 🚀 WARP ENGINE — hash rápido + dedup + abort keyed per payload
// ═══════════════════════════════════════════════════════════════

/**
 * Hash djbd2 de path+payload. Ultra-rápido (no crypto), suficiente
 * para evitar colisiones en ventanas cortas de requests.
 */
const _hashPayload = (path, params) => {
  const str = path + '|' + JSON.stringify(params || {});
  let h = 5381;
  for (let i = 0, len = str.length; i < len; i++) {
    h = ((h << 5) + h) ^ str.charCodeAt(i);
    h |= 0;
  }
  return (h >>> 0).toString(36);
};

// ─── Hooks estables para queries y configuraciones ──────────────
const useDashboardQuery = (path, executeDashboard) =>
  useCallback((params) => executeDashboard(path, params), [executeDashboard, path]);

const useDashboardConfig = (path, fetchConfig, defaultForceRefresh = false) =>
  useCallback(
    (forceRefresh) => fetchConfig(path, forceRefresh !== undefined ? forceRefresh : defaultForceRefresh),
    [fetchConfig, defaultForceRefresh, path]
  );


export const QueryProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { profile } = useAuth();

  // Abort controllers activos (keyed por path::hash(payload))
  const abortControllers = useRef({});
  // Contador de requests activos
  const activeRequests = useRef(0);
  // Flag de desmontaje
  const unmountedRef = useRef(false);
  // Track de promises activas (dedup in-flight)
  const inFlightRef = useRef({});

  // Cleanup al desmontar
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      Object.values(abortControllers.current).forEach((c) => c?.abort?.());
      abortControllers.current = {};
      inFlightRef.current = {};
    };
  }, []);

  // ─── CORE: ejecutar query con parquet / json ──────────────────
  const executeDashboard = useCallback(async (path, params) => {
    const payloadHash = _hashPayload(path, params);
    const controllerKey = `${path}::${payloadHash}`;

    // 🔒 Abortar SOLO la request anterior con el MISMO payload.
    //    Requests distintas (p. ej. Promise.all) corren en paralelo.
    if (abortControllers.current[controllerKey]) {
      abortControllers.current[controllerKey].abort();
    }

    const controller = new AbortController();
    abortControllers.current[controllerKey] = controller;

    activeRequests.current += 1;
    setLoading(true);
    setError(null);

    try {
      const payload = { ...params, format: 'parquet' };
      const response = await apiClient.post(path, payload, {
        responseType: 'arraybuffer',
        signal: controller.signal,
      });

      const contentType = response.headers?.['content-type'] || '';
      if (contentType.includes('application/json') || response.data.byteLength < 100) {
        try {
          const text = new TextDecoder().decode(response.data);
          const t = text.trim();
          if (t.startsWith('{') || t.startsWith('[')) {
            return JSON.parse(text);
          }
        } catch { /* ignore */ }
      }

      const loader = isDirectParquetPath(path) ? loadParquetDirect : loadParquetZip;
      return await loader(response.data);
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') return null;

      let errorMsg = err.message;
      if (err.response?.data instanceof ArrayBuffer) {
        try {
          const decoded = JSON.parse(new TextDecoder().decode(err.response.data));
          errorMsg = decoded.detail || decoded.message || errorMsg;
        } catch { /* ignore */ }
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      }

      if (!unmountedRef.current) setError(errorMsg);
      console.error(`Error en ${path}:`, errorMsg);
      throw err;
    } finally {
      activeRequests.current = Math.max(0, activeRequests.current - 1);
      if (activeRequests.current === 0 && !unmountedRef.current) {
        setLoading(false);
      }
      // ⚠️ Solo limpiamos si el controller sigue siendo el nuestro
      //    (evita borrar un controller más nuevo con el mismo hash)
      if (abortControllers.current[controllerKey] === controller) {
        delete abortControllers.current[controllerKey];
      }
    }
  }, []);

  // ─── CONFIG con cache TTL ─────────────────────────────────────
  const fetchConfig = useCallback(async (path, forceRefresh = false) => {
    if (!forceRefresh) {
      const cached = CONFIG_CACHE.get(path);
      if (cached && (Date.now() - cached.ts < CONFIG_TTL)) {
        return cached.data;
      }
    }

    try {
      const url = forceRefresh
        ? `${path}${path.includes('?') ? '&' : '?'}_t=${Date.now()}`
        : path;
      const response = await apiClient.get(url);
      CONFIG_CACHE.set(path, { data: response.data, ts: Date.now() });
      return response.data;
    } catch (err) {
      console.error(`Error cargando config en ${path}:`, err);
      const cached = CONFIG_CACHE.get(path);
      return cached ? cached.data : null;
    }
  }, []);

  // ─── TRADE ────────────────────────────────────────────────────
  const postTradeFormulario = useCallback(async (datosFormulario) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post(
        '/trade/execute/cargaform',
        datosFormulario,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (err) {
      console.error('Error en postTradeFormulario:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Fallo crítico al guardar';
      setError(errorMsg);
      return { success: false, detail: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateTradeRegistro = useCallback(async (datosActualizados) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.put('/trade/execute/update', datosActualizados);
      return response.data;
    } catch (err) {
      console.error('Error en updateTradeRegistro:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Fallo al actualizar la fila';
      setError(errorMsg);
      return { success: false, detail: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  // ─── ASISTENTE T500 ───────────────────────────────────────────
  const askT500 = useCallback(async (message, userContext = null) => {
    try {
      const payload = {
        message,
        user_context: userContext || {
          id: profile?.id,
          username: profile?.username,
          nombre: profile?.nombre,
          cargo: profile?.cargo,
        },
      };
      const response = await apiClient.post('/t500/chat', payload);
      return response.data;
    } catch (err) {
      console.error('Error en asistente T500:', err);
      return {
        status: 'error',
        response: err.response?.data?.detail || 'Hubo un problema de conexión con el asistente T500.',
      };
    }
  }, [profile?.cargo, profile?.id, profile?.nombre, profile?.username]);

  // ═══════════════════════════════════════════════════════════════
  // QUERIES
  // ═══════════════════════════════════════════════════════════════
  const salesQueryPML          = useDashboardQuery('/salesv2/execute/pml', executeDashboard);
  const salesQueryActivation   = useDashboardQuery('/salesv2/execute/activation', executeDashboard);
  const salesQueryBultosUSD    = useDashboardQuery('/salesv2/execute/bultos_usd', executeDashboard);
  const salesQueryAsesores     = useDashboardQuery('/salesv2/execute/asesores', executeDashboard);
  const salesQuerySugeridos    = useDashboardQuery('/salesv2/execute/sugeridos', executeDashboard);
  const salesQueryReporteAV    = useDashboardQuery('/salesv2/execute/reporte-completo', executeDashboard);
  const salesQueryFarmatodo    = useDashboardQuery('/salesv2/execute/farmatodo', executeDashboard);

  const factQuery              = useDashboardQuery('/fact/execute/detallefact', executeDashboard);
  const kpifactQuery           = useDashboardQuery('/fact/execute/kpifact', executeDashboard);

  const sotradQuery            = useDashboardQuery('/sellout-trad/execute', executeDashboard);
  const somodQuery             = useDashboardQuery('/sellout_mod/execute', executeDashboard);

  const inventarioQuery                    = useDashboardQuery('/inventario/execute', executeDashboard);
  const inventarioFarmatodoTablaQuery      = useDashboardQuery('/inventario-farmatodo/tabla', executeDashboard);
  const inventarioFarmatodoGraficoQuery    = useDashboardQuery('/inventario-farmatodo/grafico', executeDashboard);

  const tradeflowQuery         = useDashboardQuery('/trade/execute/flow', executeDashboard);

  // ─── COBRANZAS ────────────────────────────────────────────────
  const cobranzasQueryKpis                  = useDashboardQuery('/cobranzas/execute/kpis', executeDashboard);
  const cobranzasQueryDetalle               = useDashboardQuery('/cobranzas/execute/detalle', executeDashboard);
  const cobranzasQueryResumenEspecialista   = useDashboardQuery('/cobranzas/execute/resumen_especialista', executeDashboard);
  const cobranzasQueryResumenRegion         = useDashboardQuery('/cobranzas/execute/resumen_region', executeDashboard);
  const cobranzasQueryTrend                 = useDashboardQuery('/cobranzas/execute/trend', executeDashboard);
  const cobranzasQueryTopClientes           = useDashboardQuery('/cobranzas/execute/top_clientes', executeDashboard);
  const cobranzasQueryParetoEspecialista    = useDashboardQuery('/cobranzas/execute/pareto_especialista', executeDashboard);

  // ─── CXC COBRANZAS ────────────────────────────────────────────
  const cxccobQueryDashboard          = useDashboardQuery('/cobranzas/cxccob/dashboard', executeDashboard);
  const cxccobQueryKpis               = useDashboardQuery('/cobranzas/cxccob/kpis', executeDashboard);
  const cxccobQueryPorVencimiento     = useDashboardQuery('/cobranzas/cxccob/por_vencimiento', executeDashboard);
  const cxccobQueryDetalle            = useDashboardQuery('/cobranzas/cxccob/detalle', executeDashboard);
  const cxccobQueryTopClientes        = useDashboardQuery('/cobranzas/cxccob/top_clientes', executeDashboard);
  const cxccobQueryResumenVendedor    = useDashboardQuery('/cobranzas/cxccob/resumen_vendedor', executeDashboard);
  const cxccobQueryParetoClientes     = useDashboardQuery('/cobranzas/cxccob/pareto_clientes', executeDashboard);
  const cxccobQueryTrendVencimiento   = useDashboardQuery('/cobranzas/cxccob/trend_vencimiento', executeDashboard);

  // ═══════════════════════════════════════════════════════════════
  // 📋 NOTAS DE CRÉDITO
  // ═══════════════════════════════════════════════════════════════
  const ncQueryPanel              = useDashboardQuery('/notas-credito/panel',                  executeDashboard);
  const ncQueryDetalle            = useDashboardQuery('/notas-credito/detalle',                executeDashboard);
  const ncQueryDevTabla           = useDashboardQuery('/notas-credito/devoluciones-tabla',     executeDashboard);
  const ncQueryVentaVsDev         = useDashboardQuery('/notas-credito/venta-vs-devoluciones',  executeDashboard);  // 🆕

  // ─── CONFIGS (con cache) ──────────────────────────────────────
  const getSalesFilters          = useDashboardConfig('/salesv2/config', fetchConfig);
  const getCatalogoConfig        = useDashboardConfig('/salesv2/catalogo-config', fetchConfig);
  const getFactFilters           = useDashboardConfig('/fact/config', fetchConfig);
  const getSotradFilters         = useDashboardConfig('/sellout-trad/config', fetchConfig);
  const getSomodFilters          = useDashboardConfig('/sellout_mod/config', fetchConfig);
  const getInventarioFilters     = useDashboardConfig('/inventario/config', fetchConfig);
  const getCobranzasFilters      = useDashboardConfig('/cobranzas/config', fetchConfig);
  const getCxccobFilters         = useDashboardConfig('/cobranzas/cxccob/config', fetchConfig, true);
  const getTradeCatalogos        = useDashboardConfig('/trade/catalogos', fetchConfig);
  const getTradeFilters          = useDashboardConfig('/trade/config', fetchConfig);
  const getPedidosCatalogos      = useDashboardConfig('/pedidosapp/catalogos_pedidos', fetchConfig);
  const getNotasCreditoFilters   = useDashboardConfig('/notas-credito/config', fetchConfig);
  const health                   = useDashboardConfig('/health', fetchConfig);

  const getPedidosFilters = useCallback(async (params = {}) => {
    try {
      const q = new URLSearchParams();
      if (params.fec_desde) q.append('fec_desde', params.fec_desde);
      if (params.fec_hasta) q.append('fec_hasta', params.fec_hasta);
      const queryStr = q.toString() ? `?${q.toString()}` : '';
      const response = await apiClient.get(`/pedidosapp/filtros_pedidos${queryStr}`);
      return response.data;
    } catch (err) {
      console.error('Error al obtener filtros de pedidos:', err);
      return { success: false, availableOptions: {} };
    }
  }, []);

  const getProductosCliente = useCallback(
    (clienteId, forceRefresh = false) =>
      fetchConfig(`/pedidosapp/productos_cliente?cliente_id=${clienteId}`, forceRefresh),
    [fetchConfig]
  );

  // ═══════════════════════════════════════════════════════════════
  // 📦 ALMACÉN / NOTAS DE ENTREGA
  // ═══════════════════════════════════════════════════════════════
  const getNotasEntrega = useCallback(async (params = {}) => {
    try {
      const response = await apiClient.get('/almacen/notas-entrega', { params });
      return response.data;
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') return null;
      console.error('Error al obtener notas de entrega:', err);
      const message = err.response?.data?.detail || err.message || 'No se pudieron cargar las notas de entrega.';
      throw new Error(message);
    }
  }, []);

  const getNotasEntregaFiltros = useCallback(
    () => fetchConfig('/almacen/notas-entrega/filtros'),
    [fetchConfig]
  );

  const getNotasEntregaAutocomplete = useCallback(async (campo, q = '', limit = 30) => {
    const key = `${campo}|${String(q || '').trim()}|${limit}`;
    const cached = AUTOCOMPLETE_CACHE.get(key);
    if (cached && Date.now() - cached.ts < AUTOCOMPLETE_TTL) {
      return cached.data;
    }
    try {
      const response = await apiClient.get('/almacen/notas-entrega/autocomplete', {
        params: { campo, q: String(q || '').trim(), limit },
      });
      const payload = response.data?.data || [];
      AUTOCOMPLETE_CACHE.set(key, { data: payload, ts: Date.now() });
      return payload;
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') return [];
      console.error(`Error en autocomplete "${campo}":`, err);
      return [];
    }
  }, []);

  // ─── RECLUTAMIENTO ────────────────────────────────────────────
  const getVacantes = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams();
      if (filters.status && filters.status !== 'Todos')               query.append('status', filters.status);
      if (filters.departamento && filters.departamento !== 'Todos')   query.append('departamento', filters.departamento);
      if (filters.region && filters.region !== 'Todas')               query.append('region', filters.region);
      if (filters.region_ventas && filters.region_ventas !== 'Todas') query.append('region_ventas', filters.region_ventas);
      if (filters.search)                                             query.append('search', filters.search);
      if (filters.limit)                                              query.append('limit', String(filters.limit));
      if (filters.offset)                                             query.append('offset', String(filters.offset));

      const response = await apiClient.get(`/reclutamiento/?${query.toString()}`);
      return response.data;
    } catch (err) {
      console.error('Error al obtener vacantes:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Error al cargar vacantes';
      setError(errorMsg);
      return { success: false, data: [], total: 0, detail: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const getVacanteById = useCallback(async (vacanteId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/reclutamiento/${vacanteId}`);
      return response.data;
    } catch (err) {
      console.error(`Error al obtener vacante ${vacanteId}:`, err);
      const errorMsg = err.response?.data?.detail || err.message || 'Error al cargar vacante';
      setError(errorMsg);
      return { success: false, detail: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const createVacante = useCallback(async (vacanteData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/reclutamiento/', vacanteData);
      return response.data;
    } catch (err) {
      console.error('Error al crear vacante:', err);
      let errorMsg = err.message;
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMsg = err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail)) {
          errorMsg = err.response.data.detail.map((d) => d.msg || d.message).join(', ');
        }
      }
      setError(errorMsg);
      return { success: false, detail: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateVacante = useCallback(async (vacanteId, vacanteData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.put(`/reclutamiento/${vacanteId}`, vacanteData);
      return response.data;
    } catch (err) {
      console.error(`Error al actualizar vacante ${vacanteId}:`, err);
      let errorMsg = err.message;
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMsg = err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail)) {
          errorMsg = err.response.data.detail.map((d) => d.msg || d.message).join(', ');
        }
      }
      setError(errorMsg);
      return { success: false, detail: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteVacante = useCallback(async (vacanteId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.delete(`/reclutamiento/${vacanteId}`);
      return response.data;
    } catch (err) {
      console.error(`Error al eliminar vacante ${vacanteId}:`, err);
      const errorMsg = err.response?.data?.detail || err.message || 'Error al eliminar vacante';
      setError(errorMsg);
      return { success: false, detail: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const getReclutamientoStats = useCallback(async () => {
    try {
      const response = await apiClient.get('/reclutamiento/stats/resumen');
      return response.data;
    } catch (err) {
      console.error('Error al obtener estadísticas de reclutamiento:', err);
      return {
        total_vacantes: 0, en_proceso: 0, cerradas: 0, canceladas: 0,
        cumplen_meta: 0, no_cumplen_meta: 0, promedio_dias: 0, promedio_entrevistados: 0,
      };
    }
  }, []);

  const getCargosDisponibles = useCallback(async () => {
    try {
      const response = await apiClient.get('/reclutamiento/cargos-disponibles', {
        params: { user_id: profile?.id },
      });
      return response.data;
    } catch (err) {
      console.error('Error al obtener cargos disponibles:', err);
      return { status: 'error', cargos: [], acceso_total: false };
    }
  }, [profile?.id]);

  const getLiderDirecto = useCallback(async (usuarioId) => {
    try {
      const response = await apiClient.get(`/reclutamiento/lider-directo/${usuarioId}`);
      return response.data;
    } catch (err) {
      console.error('Error al obtener líder directo:', err);
      return { usuario: null, lider: null, auto_aprobacion: false };
    }
  }, []);

  const getCargoInfo = useCallback(async (cargoId) => {
    try {
      const response = await apiClient.get(`/reclutamiento/cargo-info/${cargoId}`);
      return response.data;
    } catch (err) {
      console.error('Error al obtener info del cargo:', err);
      return null;
    }
  }, []);

  const getReclutamientoCatalogos = useCallback(async () => {
    try {
      const response = await apiClient.get('/reclutamiento/catalogos');
      return response.data;
    } catch (err) {
      console.error('Error al obtener catálogos de reclutamiento:', err);
      return {
        departamentos: [], regiones: [], regiones_ventas: [],
        statuses: ['En Proceso', 'Cerrada', 'Cancelada', 'Pausada'],
        canales: ['LinkedIn', 'Computrabajo', 'Referido', 'Instagram', 'GitHub', 'Behance', 'Otro'],
      };
    }
  }, []);

  // ─── PEDIDOS / COTIZACIÓN ─────────────────────────────────────
  const crearCotizacion = useCallback(async (orderData, ocFile = null, ocFileName = null) => {
    try {
      const formData = new FormData();
      formData.append('pedido_data', JSON.stringify(orderData));
      if (ocFile) {
        formData.append('oc_file', ocFile, ocFileName || 'orden_compra.pdf');
      }
      const response = await apiClient.post('/pedidosapp/crear_cotizacion_test', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (err) {
      console.error('Error al crear cotización:', err);
      let errorMsg = err.message;
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMsg = err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail)) {
          errorMsg = err.response.data.detail.map((d) => d.msg || d.message).join(', ');
        }
      }
      throw new Error(errorMsg);
    }
  }, []);

  const _buildPedidosQuery = (params = {}) => {
    const q = new URLSearchParams();
    const push = (k, v) => {
      if (v === undefined || v === null) return;
      if (Array.isArray(v)) q.append(k, v.join(','));
      else q.append(k, String(v));
    };
    push('status', params.status);
    push('fact_num', params.fact_num);
    push('usuario', params.usuario);
    push('cliente', params.cliente);
    push('vendedor', params.vendedor);
    push('status_list', params.status_list);
    push('nombre_cliente', params.nombre_cliente);
    push('dias', params.dias);
    push('fec_desde', params.fec_desde);
    push('fec_hasta', params.fec_hasta);
    push('page', params.page);
    push('page_size', params.page_size);
    push('buscar', params.buscar);
    return q.toString();
  };

  const getMisPedidos = useCallback(async (params = {}) => {
    try {
      const response = await apiClient.get(`/pedidosapp/mis_pedidos?${_buildPedidosQuery(params)}`);
      return response.data;
    } catch (err) {
      console.error('Error al obtener mis pedidos:', err);
      return { success: false, data: [], total_count: 0, total_bs: 0, total_usd: 0, total_usd_procesado: 0 };
    }
  }, []);

  const getKPIPedidos = useCallback(async (params = {}) => {
    try {
      const response = await apiClient.get(`/pedidosapp/kpi_pedidos?${_buildPedidosQuery(params)}`);
      return response.data;
    } catch (err) {
      console.error('Error al obtener KPIs de pedidos:', err);
      return { success: false, total_pedidos: 0, total_bs: 0.0, total_usd: 0.0 };
    }
  }, []);

  const getDetallePedido = useCallback(async (factNum) => {
    try {
      const response = await apiClient.get(`/pedidosapp/detalle_pedido/${factNum}`);
      return response.data;
    } catch (err) {
      console.error(`Error al obtener detalle del pedido ${factNum}:`, err);
      return { success: false, items: [] };
    }
  }, []);

  const updatePedidoEstado = useCallback(async ({ fact_num, nuevo_estado, cargo, username }) => {
    try {
      const response = await apiClient.post('/pedidosapp/pedido/estado', {
        fact_num, nuevo_estado, cargo, username,
      });
      return response.data;
    } catch (err) {
      console.error('Error al actualizar el estado del pedido:', err);
      const message = err.response?.data?.detail || err.message || 'No se pudo actualizar el estado del pedido.';
      throw new Error(message);
    }
  }, []);

  // ─── IMÁGENES DE PRODUCTOS ────────────────────────────────────
  const getProductoImagenUrl = useCallback((sku) => {
    if (!sku) return '/placeholder-product.png';
    return `${apiClient.defaults.baseURL}/pedidosapp/producto_imagen/${String(sku).trim()}`;
  }, []);

  const getProductoImagenBase64 = useCallback(async (sku) => {
    try {
      const response = await apiClient.get(`/pedidosapp/producto_imagen_base64/${sku}`);
      return response.data;
    } catch (err) {
      console.error(`Error al obtener imagen base64 para ${sku}:`, err);
      return { success: false, imagen_base64: null };
    }
  }, []);

  const productoImagenExiste = useCallback(async (sku) => {
    try {
      const response = await apiClient.get(`/pedidosapp/producto_imagen_existe/${sku}`);
      return response.data;
    } catch (err) {
      console.error(`Error al verificar imagen para ${sku}:`, err);
      return { success: false, existe: false };
    }
  }, []);

  const getProductosImagenesLista = useCallback(async () => {
    try {
      const response = await apiClient.get('/pedidosapp/productos_imagenes_lista');
      return response.data;
    } catch (err) {
      console.error('Error al listar imágenes:', err);
      return { success: false, total: 0, imagenes: [] };
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // 📥 EXPORTACIÓN CSV NOTAS DE CRÉDITO
  // ═══════════════════════════════════════════════════════════════
  const ncExportar = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/notas-credito/exportar', params, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const disposition = response.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      link.download = match
        ? match[1]
        : `notas_credito_${new Date().toISOString().slice(0, 10)}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, filename: link.download };
    } catch (err) {
      console.error('Error exportando notas de crédito:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'No se pudo exportar el CSV.';
      setError(errorMsg);
      return { success: false, detail: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  // ─── invalidar cache de configs ───────────────────────────────
  const invalidateConfigCache = useCallback((path = null) => {
    if (path) {
      for (const key of Array.from(CONFIG_CACHE.keys())) {
        if (key === path || key.startsWith(path) || key.includes(path)) {
          CONFIG_CACHE.delete(key);
        }
      }
      if (path.includes('/notas-entrega/autocomplete')) {
        AUTOCOMPLETE_CACHE.clear();
      }
    } else {
      CONFIG_CACHE.clear();
      AUTOCOMPLETE_CACHE.clear();
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // VALUE — Memoizado
  // ═══════════════════════════════════════════════════════════════
  const value = useMemo(() => ({
    loading,
    error,

    // Ventas
    getSalesFilters,
    salesQueryPML, salesQueryActivation, salesQueryBultosUSD,
    salesQueryAsesores, salesQuerySugeridos, salesQueryReporteAV,
    salesQueryFarmatodo, getCatalogoConfig,

    // Facturación
    factQuery, kpifactQuery, getFactFilters,

    // Sellout
    sotradQuery, getSotradFilters,
    somodQuery, getSomodFilters,

    // Inventario
    inventarioQuery, getInventarioFilters,
    inventarioFarmatodoTablaQuery, inventarioFarmatodoGraficoQuery,

    // Cobranzas
    getCobranzasFilters, cobranzasQueryKpis, cobranzasQueryDetalle,
    cobranzasQueryResumenEspecialista, cobranzasQueryResumenRegion,
    cobranzasQueryTrend, cobranzasQueryTopClientes, cobranzasQueryParetoEspecialista,

    // 💰 CXC Cobranzas
    getCxccobFilters, cxccobQueryDashboard, cxccobQueryKpis,
    cxccobQueryPorVencimiento, cxccobQueryDetalle, cxccobQueryTopClientes,
    cxccobQueryResumenVendedor, cxccobQueryParetoClientes, cxccobQueryTrendVencimiento,

    // 📋 Notas de Crédito
    getNotasCreditoFilters,
    ncQueryPanel, ncQueryDetalle, ncQueryDevTabla, ncQueryVentaVsDev, ncExportar,

    // Trade
    getTradeCatalogos, getTradeFilters, tradeflowQuery,
    postTradeFormulario, updateTradeRegistro,

    // Asistente
    askT500, health,

    // Pedidos
    getPedidosCatalogos, getProductosCliente, crearCotizacion,
    getMisPedidos, getKPIPedidos, getDetallePedido,
    getPedidosFilters, updatePedidoEstado,

    // 📦 Almacén / Notas de entrega
    getNotasEntrega, getNotasEntregaFiltros, getNotasEntregaAutocomplete,

    // Imágenes
    getProductoImagenUrl, getProductoImagenBase64,
    productoImagenExiste, getProductosImagenesLista,

    // Reclutamiento
    getVacantes, getVacanteById, createVacante, updateVacante, deleteVacante,
    getReclutamientoStats, getReclutamientoCatalogos,
    getCargosDisponibles, getLiderDirecto, getCargoInfo,

    // Utilidades
    invalidateConfigCache,
  }), [
    loading, error,

    getSalesFilters, getCatalogoConfig,
    salesQueryPML, salesQueryActivation, salesQueryBultosUSD,
    salesQueryAsesores, salesQuerySugeridos, salesQueryReporteAV,
    salesQueryFarmatodo,

    factQuery, kpifactQuery, getFactFilters,

    sotradQuery, getSotradFilters,
    somodQuery, getSomodFilters,

    inventarioQuery, getInventarioFilters,
    inventarioFarmatodoTablaQuery, inventarioFarmatodoGraficoQuery,

    getCobranzasFilters, cobranzasQueryKpis, cobranzasQueryDetalle,
    cobranzasQueryResumenEspecialista, cobranzasQueryResumenRegion,
    cobranzasQueryTrend, cobranzasQueryTopClientes, cobranzasQueryParetoEspecialista,

    getCxccobFilters, cxccobQueryDashboard, cxccobQueryKpis,
    cxccobQueryPorVencimiento, cxccobQueryDetalle, cxccobQueryTopClientes,
    cxccobQueryResumenVendedor, cxccobQueryParetoClientes, cxccobQueryTrendVencimiento,

    getNotasCreditoFilters,
    ncQueryPanel, ncQueryDetalle, ncQueryDevTabla, ncQueryVentaVsDev, ncExportar,

    getTradeCatalogos, getTradeFilters, tradeflowQuery,
    postTradeFormulario, updateTradeRegistro,

    askT500, health,

    getPedidosCatalogos, getProductosCliente, crearCotizacion,
    getMisPedidos, getKPIPedidos, getDetallePedido,
    getPedidosFilters, updatePedidoEstado,

    getNotasEntrega, getNotasEntregaFiltros, getNotasEntregaAutocomplete,

    getProductoImagenUrl, getProductoImagenBase64,
    productoImagenExiste, getProductosImagenesLista,

    getVacantes, getVacanteById, createVacante, updateVacante, deleteVacante,
    getReclutamientoStats, getReclutamientoCatalogos,
    getCargosDisponibles, getLiderDirecto, getCargoInfo,

    invalidateConfigCache,
  ]);

  return (
    <QueryContext.Provider value={value}>
      {children}
    </QueryContext.Provider>
  );
};